// SPDX-License-Identifier: Apache-2.0

// Shared scaffolding for the Iris wizard panels.
//
//   <Stepper>      pill-row at the top of each panel
//   <ActionRow>    back / primary button row
//   <SuccessBox>   green outlined message
//   <ErrorBox>     red outlined message
//
// Kept separate so each panel stays under 250 lines per brief. Plain SolidJS,
// no extra deps.

import { Show, type JSX } from "solid-js";

// ─────────────────────────────────────────────────────────────────────

export interface StepperProps {
  total: number;
  current: number;
  labels: ReadonlyArray<string>;
}

export function Stepper(props: StepperProps): JSX.Element {
  const items: number[] = [];
  for (let i = 1; i <= props.total; i++) items.push(i);
  return (
    <ol class="iris-wiz__steps">
      {items.map((n) => (
        <li
          classList={{
            "iris-wiz__step": true,
            "iris-wiz__step--active": props.current === n,
            "iris-wiz__step--done": props.current > n,
          }}
        >
          <span class="iris-wiz__step-num">{n}</span>
          <span class="iris-wiz__step-label">{props.labels[n - 1] ?? ""}</span>
        </li>
      ))}
    </ol>
  );
}

// ─────────────────────────────────────────────────────────────────────

export interface ActionRowProps {
  onBack?: () => void;
  primaryLabel: string;
  primaryDisabled?: boolean;
  primaryTestId?: string;
  onPrimary: () => void;
}

export function ActionRow(props: ActionRowProps): JSX.Element {
  return (
    <div class="iris-wiz__actions">
      <Show when={props.onBack}>
        <button type="button" class="iris-wiz__ghost" onClick={() => props.onBack?.()}>
          ← back
        </button>
      </Show>
      <button
        type="button"
        class="iris-wiz__primary"
        data-testid={props.primaryTestId}
        disabled={props.primaryDisabled}
        onClick={() => props.onPrimary()}
      >
        {props.primaryLabel}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

export interface BoxProps {
  testId?: string;
  children: JSX.Element;
}

export function SuccessBox(props: BoxProps): JSX.Element {
  return (
    <div class="iris-wiz__success" data-testid={props.testId}>
      {props.children}
    </div>
  );
}

export function ErrorBox(props: BoxProps): JSX.Element {
  return (
    <div class="iris-wiz__error" data-testid={props.testId}>
      {props.children}
    </div>
  );
}
